import React, { useState, useEffect } from "react";
import qs from "query-string";
import moment from "moment";
import { useSelector } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { object, string, number, array, date, bool } from "yup";
import { useHistory, useParams, withRouter } from "react-router-dom";
import { Row, Card, Form, Space, Spinner } from "@masterteam/mt-design-react";

import api from "../../providers/api";
import { addToast, cleanKPIMeasures } from "../../helpers";

import Steps from "../Steps";
import Header from "../KPIChangeDetails/Header";
import History from "../KPIChangeDetails/History";
import FormHeader from "../FormHeader";
import RequestDetails from "../KPIChangeDetails/RequestDetails";
import ConfirmationModal from "../ConfirmationModal";
import ChangeKPITarget from "./ChangeKpiTarget";

import "./style.scss";

const validationSchema = () =>
  object().shape({
    RequestDescription: string().required(" "),
    Baseline: number().when(["HasNoTarget", "HasNoBaseline"], {
      is: (hasNoTarget, hasNoBaseline) => hasNoTarget || hasNoBaseline,
      then: (schema) => schema.nullable().min(0, " "),
      otherwise: (schema) => schema.required(" ").min(0, " ")
    }),

    Years: number()
      .required(" ")
      .min(1, " ")
      .max(5, " ")
      .when("Frequency", {
        is: (frequency) => frequency?.Value === "custom",
        then: (schema) => schema.nullable().notRequired()
      }),
    HasNoTarget: bool(),
    HasNoBaseline: bool(),
    CustomFrequency: string()
      .nullable()
      .when("Frequency", {
        is: (frequency) => frequency?.Value === "custom",
        then: (schema) => schema.required(" ")
      }),
    Frequency: object().required(" ").nullable(),
    StartDate: date().required(" "),
    BaselineDate: date().required(" "),
    KPIMeasures: array()
      .min(1, " ")
      .when(
        ["Polarity", "CalculationMethod", "HasNoTarget", "HasNoBaseline"],
        (polarity, calculationMethod, hasNoTarget, hasNoBaseline, schema) => {
          const shape = {};

          shape.Target = number().when("IsEditable", {
            is: true,
            then: (targetSchema) =>
              hasNoTarget || hasNoBaseline
                ? targetSchema.nullable().min(0, " ")
                : targetSchema.required(" ").min(0, " "),
            otherwise: (targetSchema) => targetSchema.nullable().min(0, " ")
          });

          if (calculationMethod?.Value === "4") {
            shape.MaxTarget = number()
              .required(" ")
              .when(["Target"], (target, sch) => {
                if (target !== undefined && target !== null) {
                  return sch.min(target, " ");
                }
                return sch.min(0, " ");
              });
          }
          return schema.of(object().shape(shape));
        }
      )
  });
const KPIChangeMeasurements = ({ location }) => {
  const intl = useIntl();
  const { kpi, type } = qs.parse(location.search);
  const { id } = useParams();
  const history = useHistory();
  const [kpiDetails, setKpiDetails] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [kpiDetailsLoading, setKpiDetailsLoading] = useState(false);
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [
    KPIChangeType,
    user,
    objectiveLevels,
    polarities,
    frequencies,
    calculationMethods
  ] = useSelector(({ theme, user }) => [
    theme.ChangeRequestChangeType.item?.Items,
    user.user,
    theme?.ObjectiveLevel?.item?.Items,
    theme.Polarities.item.Items,
    theme.Frequencies.item.Items,
    theme.CalculationMethods.item.Items
  ]);

  const [lists, setLists] = useState({
    users: [],
    themes: [],
    pillars: []
  });

  const [initialValues, setInitialValues] = useState({
    ChangeType: type,
    CreatedBy: user.DisplayName,
    Created: new Date(),
    RequestDescription: "",
    Baseline: "",
    BaselineDate: new Date(),
    StartDate: new Date(),
    Years: "2",
    HasNoTarget: false,
    HasNoBaseline: false,
    CustomFrequency: "",
    Frequency: frequencies[0],
    CalculationMethod: calculationMethods[0],
    KPIMeasures: cleanKPIMeasures(2, new Date(), frequencies[0])
  });

  const getData = async () => {
    setIsLoading(true);
    try {
      if (id) {
        await getRequestData();
      }

      setIsLoading(false);
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getKpiDetails = async (id) => {
    setKpiDetailsLoading(true);
    let kpiRes;

    try {
      kpiRes = await api.kpi.getById(id);

      if (kpiRes?.StatusCode === "Success") {
        const CalculationMethod = calculationMethods.find(
          (item) => parseInt(item.Value) === kpiRes?.Data.CalculationMethod
        );

        setInitialValues({
          ...initialValues,
          Baseline: kpiRes?.Data?.Baseline,
          StartDate: new Date(kpiRes?.Data.StartDate),
          BaselineDate: new Date(kpiRes?.Data.BaselineDate),
          Years: kpiRes?.Data?.Years,
          HasNoTarget: kpiRes?.Data?.HasNoTarget,
          HasNoBaseline: kpiRes?.Data?.HasNoBaseline,
          CustomFrequency: kpiRes?.Data?.CustomFrequency,
          Frequency: frequencies.find(
            (item) =>
              item.Value?.toLowerCase() ===
              kpiRes?.Data.Frequency?.toLowerCase()
          ),
          CalculationMethod,
          Polarity: polarities.find(
            (item) => item.Value === kpiRes?.Data.Polarity
          ),
          KPIMeasures: kpiRes?.Data.KPIMeasures?.map((item, i) => ({
            ...item,
            ItemIndex: i,
            DueDate:
              kpiRes?.Data?.Frequency === "custom"
                ? new Date(item?.DueDate)
                : moment(item.DueDate).locale("en").format("MM-DD-YYYY"),
            Year: new Date(item.DueDate).getFullYear(),
            IsEditable: true
          }))
        });
        setKpiDetails(kpiRes.Data);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setKpiDetailsLoading(false);
    }

    if (kpiRes?.StatusCode === "Success") {
      return kpiRes.Data;
    }
  };

  const getRequestData = async () => {
    setRequestIsLoading(true);
    try {
      const res = await api.workflow.getRequestByID(id);
      const Form = { ...res?.Data?.Form };

      setRequestData(res?.Data);
      setIsReadOnly(res?.Data?.Status !== 7);

      const CalculationMethod = calculationMethods.find(
        (item) => parseInt(item.Value) === Form?.KPIDetails?.CalculationMethod
      );

      setKpiDetails(Form?.OldKPIDetails);
      setInitialValues({
        ...initialValues,
        ChangeType: type ?? Form?.ChangeType,
        RequestDescription: Form?.RequestDescription,
        EnglishName: Form?.KPIDetails?.EnglishName,
        Baseline: Form?.KPIDetails?.Baseline,
        StartDate: new Date(Form?.KPIDetails.StartDate),
        BaselineDate: new Date(Form?.KPIDetails.BaselineDate),
        Years: Form?.KPIDetails?.Years,
        HasNoTarget: Form?.KPIDetails?.HasNoTarget,
        HasNoBaseline: Form?.KPIDetails?.HasNoBaseline,
        CustomFrequency: Form?.KPIDetails?.CustomFrequency,
        Frequency: frequencies.find(
          (item) =>
            item.Value?.toLowerCase() ===
            Form?.KPIDetails.Frequency?.toLowerCase()
        ),
        CalculationMethod,
        Polarity: polarities.find(
          (item) => item.Value === Form?.KPIDetails.Polarity
        ),
        KPIMeasures: Form?.KPIDetails.KPIMeasures?.map((item, i) => ({
          ...item,
          ItemIndex: i,
          // DueDate: moment(item.DueDate).locale("en").format("MM-DD-YYYY"),
          DueDate:
            Form?.KPIDetails?.Frequency === "custom"
              ? new Date(item?.DueDate)
              : moment(item.DueDate).locale("en").format("MM-DD-YYYY"),
          Year: new Date(item.DueDate).getFullYear()
        })),
        CreatedBy: res?.Data?.RequestedBy?.DisplayName
      });
    } catch (err) {
      addToast.error(err.message, {
        toastId: "errorFetchingData"
      });
    } finally {
      setRequestIsLoading(false);
    }
  };

  const onSubmit = async (values, isDraft = false) => {
    setIsSubmitting(true);
    try {
      let attachments = [];

      if (values?.Attachments?.length) {
        const attachmentsRes = await api.attachment.create(values?.Attachments);
        attachments = attachmentsRes?.Data?.map((item) => ({ ID: item }));
      }

      const body = {
        RequestID: id || 0,
        KPIID: kpiDetails?.ID,
        RequestDescription: values?.RequestDescription,
        ChangeType: values?.ChangeType,
        IsDraft: isDraft,
        KPIDetails: {
          ...values,
          StartDate: moment(values.StartDate).locale("en").format("MM-DD-YYYY"),
          BaselineDate: moment(values.BaselineDate)
            .locale("en")
            .format("MM-DD-YYYY"),
          UnitOfMeasure: values?.UnitOfMeasure?.value,
          Polarity: values.Polarity?.Value,
          Frequency: values.Frequency?.Value,
          CalculationMethod: values.CalculationMethod?.Value,
          KPIMeasures:
            values.Frequency?.Value === "custom"
              ? values.KPIMeasures?.map((item, i) => ({
                  ...item,
                  DueDate: moment(item?.DueDate, "MM-DD-YYYY")
                    .locale("en")
                    .format("MM-DD-YYYY")
                }))
              : values.KPIMeasures
        },
        OldKPIDetails: { ...kpiDetails },
        Attachments: attachments
      };

      const res = await api.kpi.changeKPIRequest(body);

      const { StatusCode } = res;

      if (StatusCode === "Success") {
        addToast.success("toast.sendOperation");
        history.push(`/splus/kpi-details/${kpiDetails?.ID}`);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  async function fetchData() {
    getData();
    if (kpi) getKpiDetails(kpi);
  }

  useEffect(() => {
    fetchData();
  }, []);

  if (isLoading || kpiDetailsLoading || requestIsLoading)
    return (
      <Row justify="center" style={{ marginTop: "60px" }}>
        <Spinner size="large" />
      </Row>
    );

  return (
    <>
      <Header kpiDetails={kpiDetails} />
      <Card className="kpi-change-request">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema()}
          onSubmit={() => {
            setIsConfirmationModalOpen(true);
          }}
        >
          {({ values, setFieldValue, errors }) => (
            <>
              <FormHeader
                canApprove={requestData?.CanApprove}
                isReadOnly={isReadOnly}
                getData={() => {
                  getRequestData();
                }}
                data={requestData}
                title="change-request-form"
                cancelRoute={`/splus/kpi-details/${kpiDetails?.ID}`}
                handleSaveAsDraft={() => {
                  onSubmit(values, true);
                }}
              />

              {isReadOnly ? <Steps steps={requestData?.Steps} /> : null}

              <Space className="form-body" direction="vertical" size="large">
                <Space className="request-type-wrapper">
                  <div className="label">
                    <FormattedMessage id="change-type" /> :
                  </div>
                  <div className="request-type">
                    <FormattedMessage id="change-kpi-measures" />
                  </div>
                </Space>

                <RequestDetails
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                />

                <ChangeKPITarget
                  kpiDetails={kpiDetails}
                  isReadOnly={isReadOnly}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                />

                {isReadOnly ? <History data={requestData?.WFHistory} /> : null}
              </Space>

              {isConfirmationModalOpen && (
                <ConfirmationModal
                  isOpen={isConfirmationModalOpen}
                  title={intl.formatMessage({ id: "kpi-change-request" })}
                  closeModal={() => {
                    setIsConfirmationModalOpen(false);
                    addToast.error("toast.cancelOperation");
                  }}
                  handleSubmit={() => {
                    onSubmit(values, false);
                  }}
                  ButtonStatus={isSubmitting}
                />
              )}
            </>
          )}
        </Form>
      </Card>
    </>
  );
};

export default withRouter(KPIChangeMeasurements);
